import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import SEO from './SEO';
import './FaqPage.css';

const AUDIENCES = [
  { key: 'customer', label: 'Customer', theme: 'teal' },
  { key: 'rider', label: 'Rider', theme: 'teal' },
  { key: 'merchant', label: 'Merchant', theme: 'amber' },
  { key: 'vendor', label: 'Vendor', theme: 'emerald' },
];

const AUDIENCE_META = {
  customer: {
    title: 'Customer FAQ: Ordering, Payments & Tracking',
    description:
      'Answers for BikesDrop customers. Account setup, ordering food, groceries, Pickup Delivery, Okada rides, cargo, payments, refunds, tracking and safety.',
  },
  rider: {
    title: 'Rider FAQ: Sign Up, Earnings & Payouts',
    description:
      'Answers for BikesDrop riders. How to sign up, verification, receiving jobs, earnings and payouts, ratings, safety and your account.',
  },
  merchant: {
    title: 'Merchant FAQ: Store Setup, Orders & Payouts',
    description:
      'Answers for BikesDrop merchants. Store setup, verification, receiving orders, payments and payouts, products, staff accounts and your store rating.',
  },
  vendor: {
    title: 'Vendor FAQ: Shop Applications, Deliveries & Point of Sale',
    description:
      'Answers for BikesDrop delivery partner vendors. The difference between a vendor and a merchant, booking Bike, Cargo and Pickup Delivery, Point of Sale, staff accounts, fees and payments.',
  },
};

const FAQ_DATA = {
  customer: [
    {
      title: 'Getting Started',
      icon: 'rocket',
      items: [
        {
          q: 'How do I create a BikesDrop account?',
          a: 'Download the BikesDrop app and sign up with your phone number, email, or Google account. You’ll be asked for a few basic details before you can start ordering.',
        },
        {
          q: 'Which areas does BikesDrop currently serve?',
          a: 'BikesDrop operates across Accra and select towns in Ghana, with coverage expanding over time. The app shows what’s available at your current location.',
        },
        {
          q: 'Can I use one account for food, groceries, rides and deliveries?',
          a: 'Yes. One BikesDrop account gives you access to food ordering, grocery delivery, Pickup Delivery, Okada rides, cargo delivery and campus errands, all from the same app.',
        },
      ],
    },
    {
      title: 'Ordering',
      icon: 'shoppingBag',
      items: [
        {
          q: 'How do I order food or groceries?',
          a: 'From the home screen, choose Food or Groceries, browse stores near you, add items to your cart, and check out. You’ll see the estimated delivery time before confirming.',
        },
        {
          q: 'How do I send a package with Pickup Delivery?',
          a: 'Choose Pickup Delivery from the home screen, set your pickup and drop off points, and confirm. You’ll be matched with a nearby rider and can track the delivery live.',
        },
        {
          q: 'How do I book an Okada ride?',
          a: 'Choose Okada from the home screen, enter your pickup and destination to see the route and fare, then either pick a specific online rider or send it to the open pool for any nearby rider to accept.',
        },
        {
          q: 'How do I send a package or cargo delivery?',
          a: 'Choose Cargo Delivery, enter pickup and drop off details along with a description of the item, and confirm the request. A rider will be assigned to handle the delivery.',
        },
        {
          q: 'What are campus errands?',
          a: 'Campus errands let students on supported campuses request small tasks, printing, document pickups, and similar quick jobs, fulfilled by a nearby rider.',
        },
        {
          q: 'Can I schedule an order for later?',
          a: 'Where available, you can choose a scheduled delivery time at checkout instead of "as soon as possible". Look for the schedule option before confirming your order.',
        },
      ],
    },
    {
      title: 'Payments & Refunds',
      icon: 'creditCard',
      items: [
        {
          q: 'What payment methods are accepted?',
          a: 'BikesDrop accepts mobile money (MTN, Vodafone Cash, AirtelTigo), major debit/credit cards, and cash on delivery where enabled for your area.',
        },
        {
          q: 'How do refunds work?',
          a: 'If an order is cancelled, missing items, or has a quality issue, open a support ticket from that order. Approved refunds are credited to your BikesDrop wallet or original payment method.',
        },
        {
          q: 'Why was I charged a delivery fee?',
          a: 'Delivery fees are based on distance and demand, and are shown clearly before you confirm your order. Nothing is added afterward.',
        },
      ],
    },
    {
      title: 'Delivery & Tracking',
      icon: 'mapPin',
      items: [
        {
          q: 'How do I track my order or rider?',
          a: 'Once your order is confirmed, use the live tracking map to see your rider’s location and estimated arrival time in real time.',
        },
        {
          q: 'What if my order arrives late or incomplete?',
          a: 'Open "BikesDrop Support" from your Account tab and describe what happened. We’ll review and make it right, including a refund where appropriate.',
        },
        {
          q: 'Can I contact my rider directly?',
          a: 'Yes, use the in app chat on the tracking screen to message your rider directly.',
        },
      ],
    },
    {
      title: 'Account & Privacy',
      icon: 'fileText',
      items: [
        {
          q: 'How do I update my profile information?',
          a: 'Go to Account → Edit Profile to update your name, phone number, email, or delivery addresses.',
        },
        {
          q: 'How is my data protected?',
          a: 'BikesDrop only collects what’s needed to fulfil your orders and improve the service. See our Privacy Policy in the app for full details.',
        },
        {
          q: 'How do I delete my account?',
          a: 'Go to Account → Delete Account, give a reason and accept the deletion terms. Your account enters a 30 day grace period, during which you can ask support to restore it, before it’s permanently deleted.',
        },
        {
          q: 'Can I create a new account after deleting my old one?',
          a: 'Yes. Once your previous account is permanently deleted, you’re free to register a new BikesDrop account with the same phone number or email.',
        },
      ],
    },
    {
      title: 'Safety & Support',
      icon: 'shield',
      items: [
        {
          q: 'Is it safe to ride with BikesDrop?',
          a: 'Every rider is verified before going live on the platform, trips are tracked in real time, and in app chat lets you reach your rider directly.',
        },
        {
          q: 'How do I report an issue with an order or rider?',
          a: 'Open "BikesDrop Support" from your Account tab and open a ticket describing what happened. Our team follows up on every report.',
        },
        {
          q: 'How do I contact customer support?',
          a: 'Use "BikesDrop Support" from your Account tab in the app to open a ticket and chat with the team, or use the contact details in our footer below.',
        },
      ],
    },
  ],
  rider: [
    {
      title: 'Getting Started',
      icon: 'rocket',
      items: [
        {
          q: 'How do I sign up as a BikesDrop rider?',
          a: 'Download the BikesDrop Business app, choose "Bikes Rider" on the landing screen, and follow the registration steps: phone or email, personal details, vehicle information and your documents. Your application is reviewed before your account goes live.',
        },
        {
          q: 'What documents do I need to become a rider?',
          a: 'A valid national ID, a passport style photo, your driver’s licence (if riding a motorbike or car), vehicle registration, and roadworthy/insurance documents where applicable.',
        },
        {
          q: 'How long does verification take?',
          a: 'Most applications are reviewed within 24 to 48 hours. You’ll get a notification and email once you’re approved and able to start accepting deliveries.',
        },
        {
          q: 'What vehicles are supported?',
          a: 'Bicycles, motorbikes and cars are all supported, depending on the delivery type (Pickup Delivery, cargo, campus errands or Okada).',
        },
        {
          q: 'Can I ride part time?',
          a: 'Yes. There’s no minimum hours requirement. You go online when you want to receive requests and go offline any time.',
        },
      ],
    },
    {
      title: 'Deliveries & Orders',
      icon: 'truck',
      items: [
        {
          q: 'How do I receive delivery requests?',
          a: 'Go online from your dashboard and nearby requests across Pickup Delivery, Campus Errands, Cargo Delivery and Okada rides will start coming in, including food and grocery orders merchants book a rider for.',
        },
        {
          q: 'Can I decline a delivery request?',
          a: 'Yes. Frequent declines can lower your acceptance rate, which may affect how many requests you’re offered. But you’re never forced to accept a job.',
        },
        {
          q: 'What if I can’t find the pickup or drop off location?',
          a: 'Use the in app navigation to open the address in maps, or message the merchant/customer directly through the in app chat.',
        },
        {
          q: 'What happens if a customer isn’t available at drop off?',
          a: 'Try calling or messaging the customer through the app first. If they’re unreachable after a reasonable wait, contact BikesDrop Support so our team can advise next steps.',
        },
      ],
    },
    {
      title: 'Earnings & Payouts',
      icon: 'creditCard',
      items: [
        {
          q: 'How much can I earn per delivery?',
          a: 'Earnings are based on distance, delivery type and demand at the time. The fare is shown before you accept a request, so you always know what a job pays upfront.',
        },
        {
          q: 'When and how do I get paid?',
          a: 'Your earnings accumulate in your in app wallet after each completed delivery. You can request a payout to your registered mobile money or bank account from the Earnings tab.',
        },
        {
          q: 'Are there bonuses or incentives?',
          a: 'BikesDrop periodically runs peak hour bonuses and completion incentives, shown on your dashboard when active.',
        },
      ],
    },
    {
      title: 'Ratings & Performance',
      icon: 'trendingUp',
      items: [
        {
          q: 'How is my rider rating calculated?',
          a: 'It’s a rolling average of the ratings customers and merchants leave after each delivery, covering punctuality, communication and care of the order.',
        },
        {
          q: 'What happens if my rating drops too low?',
          a: 'A consistently low rating may temporarily reduce how many requests you’re offered, or trigger a review of your account. You’ll be notified before any action is taken.',
        },
      ],
    },
    {
      title: 'Safety',
      icon: 'shield',
      items: [
        {
          q: 'What safety features does the app provide?',
          a: 'In app chat lets you reach a customer or merchant without exchanging personal contact details, live location sharing lets support see where you are during an active delivery, and every trip is logged for accountability.',
        },
        {
          q: 'What do I do in an accident or emergency?',
          a: 'Prioritise your safety first. Get to a safe location and seek medical help if needed. Then report the incident through Support so our team can log it and follow up.',
        },
      ],
    },
    {
      title: 'Account & Documents',
      icon: 'fileText',
      items: [
        {
          q: 'How do I update my vehicle or documents?',
          a: 'Go to Profile → Documents to upload renewed licences, insurance or registration. Updated documents are verified again before they take effect.',
        },
        {
          q: 'Can I switch between rider and merchant roles on the same account?',
          a: 'Yes. The same sign in can hold both a rider profile and a merchant profile. Use the role switch on the app’s landing screen to move between them.',
        },
        {
          q: 'How do I delete my rider account?',
          a: 'Go to Profile → Delete Account. You’ll be asked for a reason and to accept the deletion terms. Your account enters a 30 day grace period before it’s permanently deleted.',
        },
      ],
    },
    {
      title: 'App & Technical Issues',
      icon: 'zap',
      items: [
        {
          q: 'The app isn’t showing new delivery requests. What do I do?',
          a: 'Confirm you’re toggled online, check your internet connection, and make sure location permission is granted. If it persists, log out and back in, or reinstall the app.',
        },
        {
          q: 'How do I contact support?',
          a: 'Use "BikesDrop Support" from your Profile to open a ticket and chat with the team.',
        },
      ],
    },
  ],
  merchant: [
    {
      title: 'Getting Started & Store Setup',
      icon: 'briefcase',
      items: [
        {
          q: 'How do I register my store/restaurant on BikesDrop?',
          a: 'Open the BikesDrop Business app, choose "Bikes Merchant" on the landing screen, and complete the store setup wizard. Business details, category, location, and your documents.',
        },
        {
          q: 'What documents are required for merchant verification?',
          a: 'A valid business registration or ID, a store/product photo, and your bank or mobile money details for payouts. Additional documents may be requested depending on your business category.',
        },
        {
          q: 'How do I add products/menu items?',
          a: 'From your merchant dashboard, go to Store → Products (or Menu) to add items with photos, prices and descriptions.',
        },
        {
          q: 'Can I run multiple stores under one account?',
          a: 'Yes. One merchant account can run as many stores as you like, each with its own profile, hours and menu or stock, and you choose exactly which staff member manages which store.',
        },
      ],
    },
    {
      title: 'Orders & Fulfillment',
      icon: 'package',
      items: [
        {
          q: 'How do I receive and accept new orders?',
          a: 'New orders appear on your dashboard with a notification and sound alert. Tap to review the items, then accept or decline within the response window shown.',
        },
        {
          q: 'How do I assign a rider to an order?',
          a: 'Once you accept an order, request a BikesDrop rider from your dashboard and a nearby available rider is matched to it. You’ll see their details and can message them directly if needed.',
        },
        {
          q: 'What if I’m out of stock on an item?',
          a: 'Mark the item unavailable from Store → Products so it stops appearing to customers, or contact the customer through the order chat to substitute or adjust the order.',
        },
      ],
    },
    {
      title: 'Payments & Payouts',
      icon: 'creditCard',
      items: [
        {
          q: 'How and when do I get paid for orders?',
          a: 'Order proceeds are added to your merchant wallet once the order is completed. You can request a payout to your mobile money or bank account from the Earnings/Payouts screen.',
        },
        {
          q: 'What commission does BikesDrop take?',
          a: 'BikesDrop takes a service commission per completed order, shown in your order breakdown. Exact rates can vary by category and are disclosed during onboarding.',
        },
        {
          q: 'How do refunds work for merchants?',
          a: 'If a customer is refunded for an order issue, the relevant amount is deducted from your wallet and explained in the transaction history. Disputed refunds can be raised with support.',
        },
      ],
    },
    {
      title: 'Products & Inventory',
      icon: 'layers',
      items: [
        {
          q: 'How do I update prices or mark items unavailable?',
          a: 'Go to Store → Products, tap an item, and edit its price or availability toggle. Changes apply immediately to what customers see.',
        },
        {
          q: 'How do I run promotions or discounts?',
          a: 'Use the Promotions section of your dashboard (where available) to set a discount or featured listing for a limited time.',
        },
      ],
    },
    {
      title: 'Staff Accounts',
      icon: 'users',
      items: [
        {
          q: 'How do I add staff to help manage my store?',
          a: 'Go to Store → Staff and invite a team member by phone/email. There’s no limit on how many staff accounts you create, and if you run more than one store, you choose exactly which store(s) each staff member is assigned to manage.',
        },
        {
          q: 'What can staff accounts do vs the owner account?',
          a: 'Staff can manage orders and products day to day, but sensitive actions, payouts, store deletion, and account level settings, are restricted to the owner account.',
        },
        {
          q: 'What happens to staff accounts if I delete my merchant account?',
          a: 'All staff accounts under your store are removed immediately when your merchant account is deleted. There’s no separate grace period for staff.',
        },
      ],
    },
    {
      title: 'Ratings & Reviews',
      icon: 'trendingUp',
      items: [
        {
          q: 'How are store ratings calculated?',
          a: 'Your store rating is a rolling average of customer ratings after each order, covering product quality, accuracy and packaging.',
        },
        {
          q: 'Can I respond to customer reviews?',
          a: 'Yes, where reviews are enabled you can reply from Store → Reviews to address feedback publicly.',
        },
      ],
    },
    {
      title: 'Account & Documents',
      icon: 'fileText',
      items: [
        {
          q: 'How do I update my business information?',
          a: 'Go to Profile → Store Details to update your business name, address, contact details or documents. Some changes may require verification again.',
        },
        {
          q: 'How do I delete my merchant account?',
          a: 'Go to Profile → Delete Account, provide a reason and accept the deletion terms. Your store enters a 30 day grace period before it and all staff accounts are permanently deleted.',
        },
      ],
    },
    {
      title: 'App & Technical Issues',
      icon: 'zap',
      items: [
        {
          q: 'I’m not receiving order notifications',
          a: 'Check that push notifications are enabled in Profile → Notifications and at the OS level for the app, and that you have a stable internet connection.',
        },
        {
          q: 'How do I contact merchant support?',
          a: 'Use "BikesDrop Support" from your Profile to open a ticket, or check the contact details in our footer below.',
        },
      ],
    },
  ],
  vendor: [
    {
      title: 'Getting Started',
      icon: 'rocket',
      items: [
        {
          q: 'How do I apply as a BikesDrop vendor?',
          a: 'Open the BikesDrop Business app, choose "Bikes Vendor" on the landing screen, and submit your shop details, pickup location and delivery destinations for review.',
        },
        {
          q: 'What is the difference between a vendor and a merchant?',
          a: 'A merchant lists their store or restaurant inside the BikesDrop app for customers to find and order from. A vendor already has their own customers, online, by phone or in person, and uses BikesDrop purely for delivery and Point of Sale. Nothing about a vendor’s business is shown to BikesDrop customers.',
        },
        {
          q: 'How long does vendor application review take?',
          a: 'Admin reviews your shop’s pickup location and delivery jurisdictions before pricing is set and Bike or Cargo Delivery can be booked. Most applications are reviewed within a few business days.',
        },
        {
          q: 'Can I add more than one shop?',
          a: 'Yes. Submit as many shop locations as you need, each with its own delivery jurisdictions and pricing.',
        },
      ],
    },
    {
      title: 'Booking Deliveries',
      icon: 'truck',
      items: [
        {
          q: 'What delivery options do vendors have?',
          a: 'Three. Bike Delivery, a fixed rate to your own approved jurisdictions. Cargo Delivery, for larger loads, with the price agreed with the rider over chat. And Pickup Delivery, any address, priced by distance, no shop approval needed at all.',
        },
        {
          q: 'Do I need an approved shop for every delivery type?',
          a: 'Only Bike Delivery and Cargo Delivery need an admin approved shop. Pickup Delivery works immediately, even for a vendor using BikesDrop only for Point of Sale with no delivery application at all.',
        },
        {
          q: 'Can I choose which rider delivers for me?',
          a: 'Yes. Every delivery type lets you either auto assign the nearest available rider or pick a specific one yourself.',
        },
        {
          q: 'Can I message my rider?',
          a: 'Yes, an in app chat opens the moment a rider is assigned, for any of the three delivery types.',
        },
        {
          q: 'Can I cancel a delivery?',
          a: 'Yes, free, at any point before it’s delivered, whether you booked it yourself or a staff member did.',
        },
      ],
    },
    {
      title: 'Point of Sale',
      icon: 'cart',
      items: [
        {
          q: 'What is Point of Sale for vendors?',
          a: 'A built in till for ringing up sales to customers who pay you in person at your own counter, completely separate from your delivery business.',
        },
        {
          q: 'Do I need an approved shop application to use Point of Sale?',
          a: 'No. Point of Sale is available the moment you sign up, independent of whether your delivery application has been approved.',
        },
        {
          q: 'Can I track stock and low inventory?',
          a: 'Yes, each Point of Sale item tracks its own quantity, with a low stock warning once it drops below a threshold.',
        },
      ],
    },
    {
      title: 'Staff Accounts',
      icon: 'users',
      items: [
        {
          q: 'How do I add staff to my vendor account?',
          a: 'Go to Profile → Staff Management and invite a team member by phone or email. There’s no limit on how many staff accounts you create.',
        },
        {
          q: 'What can staff do versus the owner account?',
          a: 'You choose exactly which shops each staff member can book deliveries from, and which Point of Sale shops they can sell from. Wallet, postpaid billing and account level settings always stay with the owner.',
        },
        {
          q: 'What happens to staff accounts if I delete my vendor account?',
          a: 'All staff accounts under your vendor account are removed immediately when it’s deleted. There’s no separate grace period for staff.',
        },
      ],
    },
    {
      title: 'Payments & Fees',
      icon: 'creditCard',
      items: [
        {
          q: 'How do I pay for deliveries?',
          a: 'Settle the fare with your rider directly, pay as you go, unless you’ve applied for postpaid billing.',
        },
        {
          q: 'What is postpaid billing?',
          a: 'An optional billing cycle that lets you settle your delivery costs on a schedule instead of per delivery. Apply for it from your Wallet.',
        },
        {
          q: 'Does cancelling a delivery ever cost me anything?',
          a: 'No. Cancelling a delivery yourself is always free, at any stage, for Bike Delivery, Cargo Delivery and Pickup Delivery alike.',
        },
        {
          q: 'What is a no show fee?',
          a: 'If a rider arrives at your shop and waits past the grace period with no one available to hand over the package, a no show fee applies to compensate the rider for the time and travel already committed. You can see and pay any outstanding fee from Profile → Outstanding Fees.',
        },
      ],
    },
    {
      title: 'Account & Documents',
      icon: 'fileText',
      items: [
        {
          q: 'How do I update my shop details?',
          a: 'Go to your shop settings to update your pickup location, jurisdictions or shop details. Some changes may need admin review again.',
        },
        {
          q: 'How do I delete my vendor account?',
          a: 'Go to Profile → Delete Account, give a reason and accept the deletion terms. Your account enters a 30 day grace period before it and all staff accounts are permanently deleted.',
        },
      ],
    },
    {
      title: 'App & Technical Issues',
      icon: 'zap',
      items: [
        {
          q: 'How do I contact vendor support?',
          a: 'Use "BikesDrop Support" from your Profile to open a ticket and chat with the team.',
        },
      ],
    },
  ],
};

function FaqItem({ item, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__q" onClick={() => setOpen((v) => !v)}>
        <span>{item.q}</span>
        <Icon name="chevronDown" size={18} className="faq-item__chevron" />
      </button>
      <div className="faq-item__a-wrap">
        <p className="faq-item__a">{item.a}</p>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const { audience: audienceParam } = useParams();
  const navigate = useNavigate();
  const audience = AUDIENCES.some((a) => a.key === audienceParam) ? audienceParam : 'customer';
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  // An unknown/missing audience segment (bare /faq, or a typo) settles on
  // the customer FAQ, same fallback as before, but the URL itself should
  // reflect that rather than silently rendering a different audience under
  // a mismatched address.
  useEffect(() => {
    if (audienceParam !== audience) navigate(`/faq/${audience}/`, { replace: true });
  }, [audienceParam, audience, navigate]);

  useEffect(() => {
    setQuery('');
    setActiveCategory(null);
  }, [audience]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && navigate('/');
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const categories = FAQ_DATA[audience] || [];
  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!q) return null;
    const out = [];
    categories.forEach((cat) => {
      cat.items.forEach((item) => {
        if (item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)) {
          out.push({ ...item, categoryTitle: cat.title, categoryIcon: cat.icon });
        }
      });
    });
    return out;
  }, [q, categories]);

  const visibleCategories =
    activeCategory === null ? categories : categories.filter((c) => c.title === activeCategory);

  const meta = AUDIENCE_META[audience];
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: categories.flatMap((cat) =>
      cat.items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      }))
    ),
  };

  return (
    <div className="faq-page">
      <SEO title={`${meta.title} | BikesDrop Help Center`} description={meta.description} path={`/faq/${audience}/`} jsonLd={jsonLd} />
      <div className="faq-page__header">
        <div className="container faq-page__header-inner">
          <Link className="faq-page__back" to="/">
            <Icon name="arrowLeft" size={18} />
            Back to site
          </Link>
          <span className="faq-page__eyebrow">
            <Icon name="helpCircle" size={14} />
            Help Center
          </span>
        </div>
      </div>

      <div className="faq-page__audiencebar">
        <div className="container faq-page__audiencebar-inner">
          {AUDIENCES.map((a) => (
            <Link
              key={a.key}
              to={`/faq/${a.key}/`}
              className={`faq-page__audience faq-page__audience--${a.theme} ${
                audience === a.key ? 'faq-page__audience--active' : ''
              }`}
            >
              {a.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="container faq-page__body">
        <div className="faq-page__intro">
          <h1>Frequently Asked Questions</h1>
          <p>
            Answers for{' '}
            {audience === 'customer'
              ? 'BikesDrop customers'
              : audience === 'rider'
              ? 'BikesDrop riders'
              : audience === 'merchant'
              ? 'BikesDrop merchants'
              : 'BikesDrop vendors'}
            ,
            search below or browse by topic.
          </p>
        </div>

        <div className="faq-page__search">
          <Icon name="search" size={18} className="faq-page__search-icon" />
          <input
            type="text"
            placeholder='Search FAQs. E.g. "refund", "delete account"…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {!q && (
          <div className="faq-page__chips">
            <button
              className={`faq-page__chip ${activeCategory === null ? 'faq-page__chip--active' : ''}`}
              onClick={() => setActiveCategory(null)}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.title}
                className={`faq-page__chip ${activeCategory === c.title ? 'faq-page__chip--active' : ''}`}
                onClick={() => setActiveCategory(c.title)}
              >
                {c.title}
              </button>
            ))}
          </div>
        )}

        <div className="faq-page__content">
          {q ? (
            results.length === 0 ? (
              <p className="faq-page__empty">No answers matched &ldquo;{query}&rdquo;. Try a different word.</p>
            ) : (
              <div className="faq-page__flat">
                {results.map((r, i) => (
                  <div key={i} className="faq-page__flat-item">
                    <span className="faq-page__flat-cat">
                      <Icon name={r.categoryIcon} size={13} />
                      {r.categoryTitle}
                    </span>
                    <FaqItem item={r} defaultOpen={results.length <= 4} />
                  </div>
                ))}
              </div>
            )
          ) : (
            visibleCategories.map((cat) => (
              <div key={cat.title} className="faq-page__category">
                <div className="faq-page__category-head">
                  <span className="faq-page__category-icon">
                    <Icon name={cat.icon} size={16} />
                  </span>
                  <h3>{cat.title}</h3>
                </div>
                {cat.items.map((item, i) => (
                  <FaqItem key={i} item={item} />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
